import { useEffect, useState } from 'react';
import { fetchPublicEconomicConfig } from '@/api/member';
import { fmtUsd } from '@/lib/money';

/**
 * The live admin values the homepage copy shows (Ron, 29 Sep 2026): position cost, payout split and
 * heartbeat interval, from `/api/public/economic-config`. One request per page load, shared by every
 * section. Until it answers — or when the owner has not saved the economics yet — the relaunch values
 * are shown, so the copy is never blank.
 */
const FALLBACK = {
  positionCost: '$0.08',
  cyclePayout: '$0.20',
  sponsorMatch: '$0.02',
  adminFee: '$0.02',
  heartbeat: '60 seconds',
};

/** 45 → "45 seconds", 60 → "60 seconds", 90 → "90 seconds", 120 → "2 minutes", 300 → "5 minutes". */
export const heartbeatText = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return FALLBACK.heartbeat;
  if (s >= 120 && s % 60 === 0) return `${s / 60} minutes`;
  return `${s} seconds`;
};

const money = (usd, fallback) => (usd === null || usd === undefined || usd === '' || !Number.isFinite(Number(usd)) ? fallback : fmtUsd(usd));

let pending = null;

const load = () => {
  if (!pending) {
    pending = fetchPublicEconomicConfig()
      .then((d) => ({
        positionCost: money(d?.position_price_usd, FALLBACK.positionCost),
        cyclePayout: money(d?.cycle_payout_usd, FALLBACK.cyclePayout),
        sponsorMatch: money(d?.sponsor_match_usd, FALLBACK.sponsorMatch),
        adminFee: money(d?.admin_fee_usd, FALLBACK.adminFee),
        heartbeat: heartbeatText(d?.heartbeat_interval_seconds),
      }))
      .catch(() => {
        // Try again on the next mount rather than caching a failure for the whole visit.
        pending = null;
        return FALLBACK;
      });
  }
  return pending;
};

const usePublicEconomics = () => {
  const [values, setValues] = useState(FALLBACK);
  useEffect(() => {
    let alive = true;
    load().then((v) => {
      if (alive) setValues(v);
    });
    return () => {
      alive = false;
    };
  }, []);
  return values;
};

export default usePublicEconomics;
